import React from 'react';
import { Icon } from 'semantic-ui-react';
/**
 * Detects the form style used for a submission based on its metrics data
 * @param metrics - The metrics object from a form submission
 * @returns The detected form style or 'classic' as default
 */
export function detectFormStyleFromMetrics(metrics) {
    if (!metrics || typeof metrics !== 'object') {
        return 'classic';
    }
    // Look for metrics that end with '_form' to identify the form style
    const formStyleMetrics = Object.keys(metrics).filter(key => key.endsWith('_form'));
    if (formStyleMetrics.length === 0) {
        return 'classic';
    }
    // Find the metric with the highest value (in case multiple styles were used)
    let maxValue = 0;
    let detectedStyle = 'classic';
    for (const metricKey of formStyleMetrics) {
        const value = metrics[metricKey];
        if (typeof value === 'number' && value > maxValue) {
            maxValue = value;
            // Extract the style name by removing '_form' suffix
            const styleName = metricKey.replace('_form', '');
            // Map backend style names to our FormStyle types
            switch (styleName) {
                case 'classic':
                    detectedStyle = 'classic';
                    break;
                case 'cards':
                    detectedStyle = 'cards';
                    break;
                case 'conversational':
                case 'webchat':
                    detectedStyle = 'conversational';
                    break;
                case 'slack':
                    detectedStyle = 'slack';
                    break;
                case 'whatsapp':
                    detectedStyle = 'whatsapp';
                    break;
                case 'chat':
                    detectedStyle = 'chat';
                    break;
                default:
                    detectedStyle = 'classic';
            }
        }
    }
    return detectedStyle;
}
/**
 * Returns the appropriate icon component for a given form style
 * Matches the icons used in the Dashboard.tsx mock
 * @param formStyle - The form style type
 * @returns JSX element with the appropriate icon and styling
 */
export function renderFormStyleIcon(formStyle) {
    switch (formStyle) {
        case 'conversational':
        case 'chat':
            return React.createElement(Icon, { name: "comment", style: { color: '#10B981' } }); // emerald-500
        case 'classic':
            return React.createElement(Icon, { name: "clipboard list", style: { color: '#6366F1' } }); // indigo-500
        case 'cards':
            return React.createElement(Icon, { name: "grid layout", style: { color: '#F97316' } }); // orange-500
        case 'slack':
            return React.createElement(Icon, { name: "slack", style: { color: '#8B5CF6' } }); // violet-500
        case 'whatsapp':
            return React.createElement(Icon, { name: "whatsapp", style: { color: '#25D366' } }); // green-500
        default:
            return React.createElement(Icon, { name: "file text", style: { color: '#6b7280' } }); // gray-500
    }
}
/**
 * Gets a human-readable label for the form style
 * @param formStyle - The form style type
 * @returns Human-readable string for the form style
 */
export function getFormStyleLabel(formStyle) {
    switch (formStyle) {
        case 'classic':
            return 'Classic';
        case 'cards':
            return 'Cards';
        case 'conversational':
            return 'Conversational';
        case 'chat':
            return 'Chat';
        case 'slack':
            return 'Slack';
        case 'whatsapp':
            return 'WhatsApp';
        default:
            return 'Form';
    }
}
